import { Pressable, StyleSheet } from "react-native";

import { tz } from "@date-fns/tz";
import { useToday } from "@stepapp/core";
import { SvgIcon, palette, spacing } from "@stepapp/ui";
import { addWeeks, endOfWeek, format, isSameWeek, startOfWeek } from "date-fns";
import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { setEndDate, setStartDate } from "../../../store/calendar";

type Direction = "left" | "right";

type Props = {
  onPress: (direction: Direction) => void;
  direction: Direction;
};

const Arrow = ({ onPress, direction }: Props) => {
  const today = useToday();
  const appDispatch = useAppDispatch();
  const configTimezone = useAppSelector(({ appConfig }) => appConfig.timezone);

  const selectedDate = useAppSelector(({ calendar }) => calendar.startDate || new Date());

  const handleArrowPress = useCallback(
    (direction: "left" | "right") => {
      onPress(direction);

      const weekOffset = direction === "left" ? -1 : 1;
      const targetWeek = addWeeks(selectedDate, weekOffset);

      const startOfTargetWeek = startOfWeek(targetWeek, { in: tz(configTimezone) });
      const endOfTargetWeek = endOfWeek(targetWeek, { in: tz(configTimezone) });

      const isInCurrentWeek = isSameWeek(today, startOfTargetWeek, { in: tz(configTimezone) });

      const stDate = isInCurrentWeek
        ? format(today, "yyyy-MM-dd", { in: tz(configTimezone) })
        : format(startOfTargetWeek, "yyyy-MM-dd", { in: tz(configTimezone) });

      appDispatch(setStartDate(stDate));
      appDispatch(setEndDate(format(endOfTargetWeek, "yyyy-MM-dd", { in: tz(configTimezone) })));
    },
    [onPress, appDispatch, selectedDate, today, configTimezone],
  );

  const isDisabled =
    isSameWeek(today, selectedDate, { in: tz(configTimezone) }) && direction === "left";

  return (
    <Pressable
      disabled={isDisabled}
      hitSlop={{
        left: spacing.unit * 2,
        right: spacing.unit * 4,
        top: spacing.unit * 2,
        bottom: spacing.unit * 2,
      }}
      onPress={() => handleArrowPress(direction)}
      style={({ pressed }) => [styles.container, { opacity: pressed ? 0.6 : 1 }]}
      testID={`arrow-${direction}`}
    >
      <SvgIcon
        color={isDisabled ? palette.common.gray : palette.common.black}
        name={`chevron-${direction}`}
        size={spacing.unit * 3}
      />
    </Pressable>
  );
};

export default Arrow;

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    height: spacing.unit * 6,
    justifyContent: "center",
    width: spacing.unit * 6,
  },
});
